import { useState, useEffect } from 'react';
import { getPositions, getOrders, getAlpacaAccountId, getAccount } from '../lib/alpaca';

const ACHIEVEMENTS = [
  {
    id: 'first_investment',
    title: 'Primera inversión',
    desc: 'Compra tu primera acción o ETF',
    icon: '🎯',
    category: 'inicio',
    check: (data) => data.totalOrders > 0,
  },
  {
    id: 'plan_activated',
    title: 'Plan activado',
    desc: 'Completa el cuestionario de inversión',
    icon: '📋',
    category: 'inicio',
    check: (data) => data.hasProfile,
  },
  {
    id: 'autopilot',
    title: 'Piloto automático',
    desc: 'Usa el robo-advisor para invertir',
    icon: '🤖',
    category: 'inicio',
    check: (data) => data.roboUsed,
  },
  {
    id: 'diversified',
    title: 'Diversificado',
    desc: 'Ten 5 o más activos diferentes',
    icon: '🧩',
    category: 'estrategia',
    check: (data) => data.totalPositions >= 5,
  },
  {
    id: 'explorer',
    title: 'Explorador',
    desc: 'Revisa 10 acciones diferentes',
    icon: '🔍',
    category: 'estrategia',
    check: (data) => (data.viewedStocks || 0) >= 10,
  },
  {
    id: 'club_100',
    title: 'Club $100',
    desc: 'Tu portafolio llega a $100 USD',
    icon: '💯',
    category: 'milestone',
    check: (data) => data.equity >= 100,
  },
  {
    id: 'club_500',
    title: 'Club $500',
    desc: 'Tu portafolio llega a $500 USD',
    icon: '🏅',
    category: 'milestone',
    check: (data) => data.equity >= 500,
  },
  {
    id: 'club_1000',
    title: 'Club $1,000',
    desc: 'Tu portafolio llega a $1,000 USD',
    icon: '🏆',
    category: 'milestone',
    check: (data) => data.equity >= 1000,
  },
  {
    id: 'ambassador',
    title: 'Embajador',
    desc: 'Invita a 1 amigo a FlowexPay',
    icon: '🤝',
    category: 'social',
    check: (data) => (data.referrals || 0) >= 1,
  },
  {
    id: 'influencer',
    title: 'Influencer',
    desc: 'Invita a 5 amigos a FlowexPay',
    icon: '⭐',
    category: 'social',
    check: (data) => (data.referrals || 0) >= 5,
  },
];

const LEVELS = [
  { name: 'Novato', minXP: 0, color: '#9CA3AF' },
  { name: 'Inversionista', minXP: 3, color: '#2A59C6' },
  { name: 'Trader', minXP: 6, color: '#7C3AED' },
  { name: 'Experto', minXP: 9, color: '#F59E0B' },
];

function getLevel(unlockedCount) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (unlockedCount >= LEVELS[i].minXP) return LEVELS[i];
  }
  return LEVELS[0];
}

function getNextLevel(unlockedCount) {
  for (let i = 0; i < LEVELS.length; i++) {
    if (unlockedCount < LEVELS[i].minXP) return LEVELS[i];
  }
  return null;
}

function getStreak() {
  try {
    const data = JSON.parse(localStorage.getItem('fp_invest_streak') || '{}');
    return data.weeks || 0;
  } catch { return 0; }
}

function trackStockView(symbol) {
  try {
    const viewed = JSON.parse(localStorage.getItem('fp_viewed_stocks') || '[]');
    if (!viewed.includes(symbol)) {
      viewed.push(symbol);
      localStorage.setItem('fp_viewed_stocks', JSON.stringify(viewed));
    }
  } catch {}
}

function getViewedCount() {
  try {
    return JSON.parse(localStorage.getItem('fp_viewed_stocks') || '[]').length;
  } catch { return 0; }
}

export default function Achievements({ userEmail }) {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const accountId = getAlpacaAccountId(userEmail);
        let equity = 0, totalPositions = 0, totalOrders = 0;

        try {
          const account = await getAccount(accountId);
          equity = parseFloat(account.equity || 0);
        } catch {}

        try {
          const positions = await getPositions(accountId);
          totalPositions = positions.length;
        } catch {}

        try {
          const orders = await getOrders('all', 100, accountId);
          totalOrders = orders.length;
        } catch {}

        const hasProfile = !!localStorage.getItem('fp_investor_profile');
        const roboUsed = !!localStorage.getItem('fp_robo_used');
        const viewedStocks = getViewedCount();
        const streak = getStreak();
        const referrals = parseInt(localStorage.getItem('fp_referrals') || '0');

        const data = { equity, totalPositions, totalOrders, hasProfile, roboUsed, viewedStocks, streak, referrals };

        const results = ACHIEVEMENTS.map((a) => ({
          ...a,
          unlocked: a.check(data),
        }));

        setAchievements(results);
      } catch (e) {
        console.error('achievements:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [userEmail]);

  const unlocked = achievements.filter((a) => a.unlocked);
  const locked = achievements.filter((a) => !a.unlocked);
  const level = getLevel(unlocked.length);
  const nextLevel = getNextLevel(unlocked.length);
  const streak = getStreak();

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'inicio', label: 'Inicio' },
    { id: 'estrategia', label: 'Estrategia' },
    { id: 'milestone', label: 'Milestones' },
    { id: 'social', label: 'Social' },
  ];

  const filtered = selectedCat === 'all' ? achievements : achievements.filter((a) => a.category === selectedCat);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-24 bg-[#F3F4F6] rounded-2xl" />
        <div className="animate-pulse h-40 bg-[#F3F4F6] rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Level + Streak banner */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2A59C6] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl">
              {level.name === 'Novato' ? '🌱' : level.name === 'Inversionista' ? '📈' : level.name === 'Trader' ? '⚡' : '👑'}
            </div>
            <div>
              <div className="text-lg font-bold">{level.name}</div>
              <div className="text-xs text-white/70">{unlocked.length} de {achievements.length} logros</div>
            </div>
          </div>
          {streak > 0 && (
            <div className="bg-white/20 rounded-xl px-3 py-2 text-center">
              <div className="text-lg font-bold">🔥 {streak}</div>
              <div className="text-[10px] text-white/70">{streak === 1 ? 'semana' : 'semanas'}</div>
            </div>
          )}
        </div>

        {/* Progress bar to next level */}
        {nextLevel && (
          <div>
            <div className="flex justify-between text-[10px] text-white/60 mb-1">
              <span>{level.name}</span>
              <span>{nextLevel.name} ({nextLevel.minXP - unlocked.length} logros más)</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${(unlocked.length / nextLevel.minXP) * 100}%` }}
              />
            </div>
          </div>
        )}
        {!nextLevel && (
          <div className="text-xs text-white/70 text-center">¡Nivel máximo alcanzado!</div>
        )}
      </div>

      {/* Category filters */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedCat(c.id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              selectedCat === c.id ? 'bg-[#2A59C6] text-white' : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Achievements grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filtered.map((a) => (
          <div
            key={a.id}
            className={`flex items-center gap-3 p-4 rounded-xl border transition ${
              a.unlocked
                ? 'bg-white border-[#E5E7EB]'
                : 'bg-[#F9FAFB] border-[#F3F4F6] opacity-60'
            }`}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl ${
              a.unlocked ? 'bg-[#F0F4FF]' : 'bg-[#F3F4F6] grayscale'
            }`}>
              {a.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#181D27]">{a.title}</span>
                {a.unlocked && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#16A34A" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="16 8.5 10.5 14 8 11.5" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </div>
              <div className="text-xs text-[#6B7280]">{a.desc}</div>
            </div>
            {!a.unlocked && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            )}
          </div>
        ))}
      </div>

      {/* Referral CTA */}
      <div className="bg-gradient-to-r from-[#F0F4FF] to-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#2A59C6] flex items-center justify-center text-white text-lg">🎁</div>
          <div>
            <div className="text-sm font-bold text-[#181D27]">Invita amigos, gana logros</div>
            <div className="text-xs text-[#6B7280]">Comparte tu código y desbloquea Embajador e Influencer</div>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex-1 bg-[#F9FAFB] rounded-xl px-4 py-2.5 text-sm text-[#181D27] font-mono font-semibold">
            FLOWEX-{(userEmail || 'USER').slice(0, 4).toUpperCase()}
          </div>
          <button
            onClick={() => {
              const code = `FLOWEX-${(userEmail || 'USER').slice(0, 4).toUpperCase()}`;
              navigator.clipboard?.writeText(`Invierte en acciones de EE.UU. desde Colombia con FlowexPay. Usa mi código: ${code} https://beta.flowexpay.com`);
            }}
            className="px-4 py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition"
          >
            Copiar
          </button>
        </div>
      </div>
    </div>
  );
}

export { trackStockView, getViewedCount, ACHIEVEMENTS };
